/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren } from 'preact';
import { Layer } from '../../UNSAFE_Layer';
import { Floating, Placement } from '../../UNSAFE_Floating';
import { useRef, useState } from 'preact/hooks';
import { styles } from './themes/useDatatip.css';
import { useId } from '../UNSAFE_useId';
import { Property } from 'csstype';

export type Dimension = { x: number; width: number; y: number; height: number };

type DatatipProps = {
  /**
   * The datatip content.
   */
  content?: ComponentChildren;

  /**
   * Whether the datatip is anchored to pointer or element.
   *
   */
  anchor: 'element' | 'pointer';

  /**
   * The offset from the anchor element or pointer.
   */
  offset?: {
    mainAxis?: number;
    crossAxis?: number;
  };

  /**
   * The border color of the datatip.
   */
  borderColor?: Property.BorderColor;

  /**
   * The placement of datatip from anchor point.
   */
  placement?: Placement;
};

/**
 * The default offset for datatip mainAxis.
 */
const DEFAULT_OFFSET = 8;

/**
 * The hook to support custom datatip in visualization components.
 */
export function useDatatip({
  content,
  placement = 'top-end-corner',
  offset,
  borderColor,
  anchor
}: DatatipProps) {
  const uniqueIdRef = useRef<string>(useId());
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const offsetValue = offset || { mainAxis: DEFAULT_OFFSET, crossAxis: -1 * DEFAULT_OFFSET };
  const [isVisible, setIsVisible] = useState(false);

  const targetRef = useRef<HTMLElement | null>(null);
  const toggleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const onPointerMove = (event: PointerEvent) => {
    setCoords({ x: event.clientX, y: event.clientY });
    setIsVisible(true);
  };

  const onFocus = (event: FocusEvent) => {
    if (event.eventPhase === Event.AT_TARGET || event.eventPhase === Event.BUBBLING_PHASE) {
      targetRef.current = event.target as HTMLElement;
      setIsVisible(true);
    }
  };

  const onPointerEnter = (event: PointerEvent) => {
    if (event.eventPhase === Event.AT_TARGET || event.eventPhase === Event.BUBBLING_PHASE) {
      targetRef.current = event.currentTarget as HTMLElement;
      setIsVisible(true);
    }
  };

  const onBlur = () => {
    setIsVisible(false);
  };

  const onContentPointerEnter = () => {
    clearTimeout(toggleTimeoutRef.current);
  };

  const onPointerLeave = () => {
    toggleTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 20);
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      event.preventDefault();
      setIsVisible(false);
    } else if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown' ||
      event.code === 'ArrowRight' ||
      event.code === 'ArrowLeft' ||
      event.code === 'Tab'
    ) {
      setIsVisible(true);
    }
  };

  const anchorRef = anchor === 'element' ? targetRef : { current: coords };
  const datatipContent =
    isVisible && content != null ? (
      <Layer>
        <Floating
          anchorRef={anchorRef}
          placement={placement}
          offsetValue={offsetValue}
          flipOptions={{ crossAxis: false, mainAxis: false }}
          shiftOptions={{ crossAxis: true, mainAxis: true }}>
          <div
            role="tooltip"
            id={uniqueIdRef.current}
            className={styles.dvtDatatipStyle}
            style={{ borderColor }}
            onPointerEnter={onContentPointerEnter}
            onPointerLeave={onPointerLeave}>
            {content}
          </div>
        </Floating>
      </Layer>
    ) : null;

  return {
    datatipContent,
    datatipProps: {
      'aria-describedby': uniqueIdRef.current,
      onKeyUp,
      onPointerLeave,
      onPointerEnter,
      onPointerMove,
      onFocus,
      onBlur
    }
  };
}
